import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { markPartnerBranchLive } from './PartnerSessionBanner';
import { partnerConnectReturnPath, readPartnerSession } from '../utils/partnerSession';

const KeetaCallback = () => {
  const { t, i18n } = useTranslation('translation');
  const isArabic = i18n.language === 'ar';
  const location = useLocation();
  const navigate = useNavigate();
  const [integrationStatus, setIntegrationStatus] = useState('integrating'); // 'integrating', 'success'
  const hasFetched = useRef(false); // Prevent duplicate API calls in dev StrictMode

  useEffect(() => {
    if (hasFetched.current) return; // Skip if already run
    hasFetched.current = true;

    const queryParams = new URLSearchParams(location.search);
    const authCode = queryParams.get('code'); // Keeta returns this as 'code'
    const rawState = queryParams.get('state');
    let storeId = null;
    let appId = null;

    try {
      const parsedState = JSON.parse(rawState);
      storeId = parsedState.storeId;
      appId = parsedState.appId;
    } catch (err) {
      console.error('Invalid state parameter:', rawState);
      return;
    }

    if (authCode && storeId && appId) {
      const token = localStorage.getItem('token');
      fetch(`${process.env.REACT_APP_BASE_URL}/keeta/auth/token`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appId: appId, // internal appId from state
          storeId: storeId,
          auth_code: authCode,
          keetaAppId: process.env.REACT_APP_KEETA_CLIENT_ID, // Keeta client app ID
        }),
      })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setIntegrationStatus('success');
            const partnerSession = readPartnerSession();
            // Partner session keeps shop-mapping in the focused shell — do not mark live yet.
            const returnTo = partnerSession?.id
              ? partnerConnectReturnPath(partnerSession.id)
              : `/add-app/${appId}`;
            if (!partnerSession?.id) {
              markPartnerBranchLive(partnerSession?.branchId || null);
            }
            setTimeout(() => {
              if (partnerSession?.id) {
                navigate(returnTo);
              } else {
                navigate(`/add-app/${appId}`, { state: { storeId } });
              }
            }, 2000);
          } else {
            console.error('Keeta integration failed:', data.message);
            navigate(`/add-app/${appId}`, {
              state: { storeId, errorMessage: data.message || t('Keeta integration failed') }
            });
            return; // prevent further code from running
          }
        })
        .catch(console.error);
    }
  }, [location.search, navigate, t]);

  return (
    <div
      dir={isArabic ? 'rtl' : 'ltr'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'transparent',
        fontFamily: isArabic ? '"Noto Sans Arabic", "Cairo", -apple-system, sans-serif' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px'
      }}
    >
      <style>
        {`
          @keyframes modernSpin {
            0% { transform: rotate(0deg) scale(1); }
            50% { transform: rotate(180deg) scale(1.1); }
            100% { transform: rotate(360deg) scale(1); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 1; }
          }
          
          @keyframes slideUp {
            0% { transform: translateY(20px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          
          @keyframes successBounce {
            0% { transform: scale(0) rotate(0deg); }
            60% { transform: scale(1.3) rotate(180deg); }
            80% { transform: scale(0.9) rotate(360deg); }
            100% { transform: scale(1) rotate(360deg); }
          }
          
          @keyframes checkDraw {
            0% { stroke-dashoffset: 100; }
            100% { stroke-dashoffset: 0; }
          }
          
          @keyframes glowPulse {
            0%, 100% { box-shadow: 0 0 20px rgba(76, 175, 80, 0.3); }
            50% { box-shadow: 0 0 40px rgba(76, 175, 80, 0.6), 0 0 60px rgba(76, 175, 80, 0.3); }
          }
        `}
      </style>

      {integrationStatus === 'integrating' && (
        <div style={{ textAlign: 'center', animation: 'slideUp 0.5s ease-out' }}>
          <div style={{
            position: 'relative',
            marginBottom: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80px',
            height: '80px',
            margin: '0 auto 40px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              border: '3px solid rgba(102, 126, 234, 0.2)',
              borderTop: '3px solid #073b4c',
              borderRadius: '50%',
              animation: 'modernSpin 2s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite',
              position: 'absolute',
              top: '0',
              left: '0'
            }}></div>
            <div style={{
              width: '60px',
              height: '60px',
              background: 'linear-gradient(45deg, #073b4c, #ff9b53)',
              borderRadius: '50%',
              animation: 'pulse 1.5s ease-in-out infinite',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 30px hsla(229, 76%, 66%, 0.30)',
              position: 'absolute',
              top: '10px',
              left: '10px'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                background: 'white',
                borderRadius: '50%',
                opacity: 0.8
              }}></div>
            </div>
          </div>
          <div style={{
            fontSize: '24px',
            color: '#000',
            fontWeight: '300',
            marginBottom: '10px',
            letterSpacing: isArabic ? '0' : '0.5px'
          }}>
            {t('Integrating with Keeta')}
          </div>
          <div style={{
            fontSize: '16px',
            color: '#000',
            fontWeight: '300'
          }}>
            {t('Please wait while we connect your account...')}
          </div>
        </div>
      )}

      {integrationStatus === 'success' && (
        <div style={{ textAlign: 'center', animation: 'slideUp 0.6s ease-out' }}>
          <div style={{
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, #4CAF50, #45a049)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '30px',
            animation: 'successBounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55), glowPulse 2s ease-in-out infinite',
            position: 'relative',
            margin: '0 auto 30px'
          }}>
            <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
              <path
                d="M20 6L9 17L4 12"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="100"
                style={{
                  animation: 'checkDraw 0.5s ease-out 0.3s forwards',
                  strokeDashoffset: '100'
                }}
              />
            </svg>
          </div>
          <div style={{
            fontSize: '28px',
            color: '#000',
            fontWeight: '600',
            marginBottom: '10px',
            letterSpacing: isArabic ? '0' : '0.5px'
          }}>
            {t('Integration Successful!')}
          </div>
          <div style={{
            fontSize: '16px',
            color: '#000',
            fontWeight: '300'
          }}>
            {t('Keeta has been successfully connected to your account')}
          </div>
        </div>
      )}
    </div>
  );
};

export default KeetaCallback;
